import * as React from 'react';

import {motion} from 'framer-motion';

import {Button} from 'shells/button/button';
import {Card} from 'shells/card/card';
import {colors} from 'shells/colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import type {EntitySummaryStatus} from 'nutshell-graphql-types';

import {SummaryFooter} from './footer/summary-footer';
import {FormattedSummaryResponseText} from './formatted-summary-response-text';
import {useGenerateEntitySummary} from './graphql/hooks/use-generate-entity-summary';
import {useGetEntitySummary} from './graphql/hooks/use-get-entity-summary';
import {SummaryHeader} from './header/summary-header';

import styles from './timeline-summary.module.css';

type Props = {
    entityId: string;
};

export function TimelineSummary(props: Props) {
    const {entitySummary, isLoading, isRefetching} = useGetEntitySummary(
        props.entityId,
        'TIMELINE'
    );
    const {generateEntitySummary, isLoading: isGeneratingSummary} = useGenerateEntitySummary();

    // Do not show anything until initial summary fetch is complete
    if (isLoading && !entitySummary) {
        return null;
    }

    const handleGenerateSummary = () => {
        generateEntitySummary(
            {entityId: props.entityId, summaryType: 'TIMELINE'},
            entitySummary ? entitySummary.id : undefined
        );
    };

    const status: EntitySummaryStatus | null | undefined = entitySummary
        ? entitySummary.status
        : null;
    const isLoadingState =
        // initial generation
        isGeneratingSummary ||
        // refetching after initial generation
        (!entitySummary && isRefetching) ||
        // regenerating a new summary
        (isGeneratingSummary && entitySummary) ||
        // summary generation is in progress
        status === 'LOADING';

    let timelineSummaryContent;
    if (isLoadingState) {
        timelineSummaryContent = (
            <div className='flex justify-center'>
                <LoadingIcon size={48} fill={colors.orangeLt} />
            </div>
        );
    } else if (entitySummary) {
        switch (entitySummary.status) {
            case 'ERRORED':
                timelineSummaryContent = (
                    <StackLegacy spacing={4} isHorizontal={true}>
                        {/*
                        In the case where the status is ERRORED and there is a summary, the summary
                        is actually a custom error message and we will display that instead
                        */}
                        {entitySummary.summary ? (
                            <div>{entitySummary.summary}</div>
                        ) : (
                            <>
                                <div>
                                    We’re sorry! Something went wrong while generating a summary of
                                    this timeline.
                                </div>
                            </>
                        )}
                        <Button
                            noPadding={true}
                            variant='text-primary'
                            onClick={handleGenerateSummary}
                        >
                            Try again
                        </Button>
                    </StackLegacy>
                );
                break;
            case 'COMPLETE':
                timelineSummaryContent = (
                    <FormattedSummaryResponseText summaryText={entitySummary.summary} />
                );
                break;
        }
    }

    if (timelineSummaryContent) {
        return (
            <div className={styles['timeline-summary']}>
                <Card hasBorder={true} hasShadow={true} leftLineColor={colors.aiBlue}>
                    <motion.div
                        initial='full'
                        animate={isLoadingState ? 'loading' : 'full'}
                        variants={{
                            full: {height: 'auto'},
                            loading: {height: 110},
                        }}
                        transition={{
                            duration: 0.3,
                            ease: 'easeInOut',
                        }}
                    >
                        <div className='pad-16'>
                            <StackLegacy spacing={8}>
                                <SummaryHeader
                                    entityId={props.entityId}
                                    entitySummaryId={entitySummary ? entitySummary.id : undefined}
                                />
                                {timelineSummaryContent}
                                {!isLoadingState && entitySummary && (
                                    <SummaryFooter
                                        entitySummary={entitySummary}
                                        refreshSummary={handleGenerateSummary}
                                    />
                                )}
                            </StackLegacy>
                        </div>
                    </motion.div>
                </Card>
            </div>
        );
    }

    return null;
}
